import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { NAV_DRAWER_ITEMS } from '~/features/dashboard';
import { DealsList } from '~/features/dashboard/components/deals-list';
import { selectDeals } from '~/features/dashboard/store/selectors';
import * as variables from '~/styles/variables.scss';
import { useHeaderColorChanger } from '~/utils/hooks/useHeaderColorChanger';
import css from './styles.module.scss';
import { DealDetails } from '~/features/dashboard/components/deal-details';
import { DealPreview } from '~/features/dashboard/components/deal-preview';
import { PreviewDealReport } from '~/features/dashboard/components/preview-deal-report';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { getDealsThunk } from '~/features/dashboard/store/actions.thunks';
import { isDealFormEditing } from '~/features/dashboard/store/actions';
import * as CreatorsListFeature from '~/features/creators-list';
import { useAppRoute } from '~/utils/hooks/useAppRoute';
import { Loader } from '~/common/loader';
import { DealsGrid } from '~/features/dashboard/components/deals-grid';
import { DownloadButton } from '~/common/download-button';
import { DealApi } from '~/api/deal.api';
import { AddDeal } from '~/features/dashboard/components/add-deal';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { filter } from 'lodash';
import { Deal } from '~/features/dashboard/store/models';
import { withStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import AppsIcon from '@material-ui/icons/Apps';
import ListIcon from '@material-ui/icons/List';
interface MyDealsProps {}

const ColorButton = withStyles(theme => ({
    root: {
        color: theme.palette.getContrastText('#000'),
        backgroundColor: '#000',
        '&:hover': {
            backgroundColor: '#000',
        },
        borderRadius: '20px',
        textTransform: 'capitalize',
    },
}))(Button);

export const MyDeals: React.FC<MyDealsProps> = () => {
    const [isLoading, setIsLoading] = useState(true);

    const [viewStyle, setViewStyle] = useState<'grid' | 'list'>('grid');

    const config = NAV_DRAWER_ITEMS.deals;
    const match = useRouteMatch<{ creatorId: string }>();
    const deals = useSelector(selectDeals);
    const isNewDeal = useRouteMatch({ path: `${match.path}/add-deal`, exact: true });
    const isDealsTable = useRouteMatch({ path: `${match.path}/table`, exact: true });
    const dispatch = useThunkDispatch();
    const route = useAppRoute();
    const goToNewDealPage = useCallback(() => route(`${match.url}/add-deal`), [match.url, route]);
    const [filteredDeals, setFilterDeals] = useState<any>(filter(deals, ['dealType', 'ACQUISITION']));
    const toggleViewStyle = () => {
        setViewStyle(prevState => (prevState === 'grid' ? 'list' : 'grid'));
    };

    useHeaderColorChanger(config.background, variables.colorGrayPrimary, '#000');
    const [value, setValue] = React.useState(0);

    const filterDeals = (allDeals: Deal[], selectedType: number): void => {
        setFilterDeals(
            allDeals.filter((deal: any) => {
                return !selectedType ? deal.dealType === 'ACQUISITION' : deal.dealType !== 'ACQUISITION';
            })
        );
    };

    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    };

    function a11yProps(index: any) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    useEffect(() => {
        if (match.params.creatorId) {
            dispatch(getDealsThunk({ creatorId: Number(match.params.creatorId) }))
                .then(data => {
                    if (data.error) {
                        route(CreatorsListFeature.FEATURE_ROUTER_PATH);
                    }
                })
                .finally(() => setIsLoading(false));
        } else {
            route(CreatorsListFeature.FEATURE_ROUTER_PATH);
        }
    }, [dispatch, route, match.params.creatorId]);

    const handleDownloadClick = useCallback(() => {
        if (match.params.creatorId) {
            DealApi.exportDeals(Number(match.params.creatorId), !value).then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'].split('; ')[0] }));
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.download = 'download.zip';
                link.click();
                window.URL.revokeObjectURL(url);
                link.remove();
            });
        }
    }, [match.params.creatorId, value]);
    const goToPreviewDeal = useCallback(() => route(`${match.url}/preview-deal-report${!value ? '/acquisition-deal-only' : ''}`), [match.url, route, value]);

    React.useEffect(() => {
        dispatch(isDealFormEditing(false));
    }, [dispatch]);

    useEffect(() => {
        filterDeals(deals, value);
    }, [deals, value]);

    const hasAcquisitions = useMemo(() => deals.some((deal: any) => deal.dealType === 'ACQUISITION'), [deals]);

    useEffect(() => {
        if (deals.length && !hasAcquisitions) {
            setValue(1);
        }
    }, [hasAcquisitions, deals]);

    return isLoading ? (
        <Loader />
    ) : (
        <article className={css.module}>
            {!!isDealsTable || !!isNewDeal ? (
                <div className={css.topBar} style={{ paddingBottom: '1px' }} onClick={() => dispatch(isDealFormEditing(false))}>
                    {isDealsTable && (
                        <div className={css.topBarActions}>
                            <AppBar className={css.appTop} position="static">
                                <Tabs value={hasAcquisitions ? value : 1} onChange={handleChange} aria-label="simple tabs example" className={css.tabs}>
                                    <Tab label={`Acquisition Deal (${deals.filter((deal: any) => deal.dealType === 'ACQUISITION').length})`} {...a11yProps(0)} className={!hasAcquisitions ? css.hidden : ''} />
                                    <Tab label={`My Deals (${deals.filter((deal: any) => deal.dealType !== 'ACQUISITION').length})`} {...a11yProps(1)} />
                                </Tabs>
                            </AppBar>
                            <ColorButton className="add-song-btn1" variant="contained" color="primary" onClick={goToNewDealPage}>
                                Add New Deal
                            </ColorButton>
                            <h3 className="grid-listbtn" onClick={toggleViewStyle} style={{ marginLeft: '1rem', cursor: 'pointer' }}>
                                {viewStyle === 'list' ? (
                                    <>
                                        <AppsIcon style={{ verticalAlign: 'middle' }} />
                                        Grid
                                    </>
                                ) : (
                                    <>
                                        <ListIcon style={{ verticalAlign: 'middle' }} />
                                        List
                                    </>
                                )}{' '}
                                View
                            </h3>
                            <DownloadButton onClick={handleDownloadClick} className={css.actionSection} />
                            <DownloadButton
                                className={css.actionSection}
                                onClick={() => goToPreviewDeal()}
                                buttonFor="preview"
                            />
                        </div>
                    )}
                </div>
            ) : null}
            <Switch>
                <Route exact path={`${match.path}/table`}>
                    {viewStyle === 'list' ? <DealsList deals={filteredDeals} /> : <DealsGrid deals={filteredDeals} />}
                </Route>
                <Route exact path={`${match.path}/table/:dealId/details`} component={DealDetails} />
                <Route exact path={`${match.path}/table/:dealId/details-preview`} component={DealPreview} />
                <Route path={`${match.path}/add-deal`}>
                    <AddDeal />
                </Route>
                <Route path={`${match.path}/preview-deal-report/:acquisitionDealOnly?`}>
                    <PreviewDealReport />
                </Route>
                <Route exact path={match.path}>
                    <Redirect to={`${match.url}/table`} />
                </Route>
            </Switch>
        </article>
    );
};
